"use client";

// Login — deliberately DIFFERENT from the legacy ERP sign-in (client
// 2026-07-09: the two versions' logins looked identical; users need to see
// at a glance which front door they're at). Legacy = white card centred on
// a #042893 sea; this one = split-screen "blueprint" layout: dark drafting
// panel (grid + roofline sketch, IBM Plex Mono annotations) beside a white
// form panel with the #0168fa brand primary. Behaviour is unchanged:
// branch/email/password against /users/v2/login, session cookie, then
// /dashboard (or the ?redirect target the middleware recorded).

import { useEffect, useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { login, fetchBranches } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/use-auth";
import type { Branch } from "@/lib/api/types";
import styles from "./login.module.css";

const mono = { fontFamily: "var(--font-plex-mono), monospace" };

/* Abstract roofline sketch — echoes the WHB logo (arch, apex, deck) drawn
   like a blueprint elevation, with a dashed dimension line underneath. */
function RooflineSketch() {
  return (
    <svg
      viewBox="0 0 640 300"
      fill="none"
      aria-hidden="true"
      className="absolute bottom-10 left-0 w-[130%] max-w-none opacity-[0.32]"
    >
      <path
        d="M8 232 C 90 148 150 128 208 126 L 322 34 L 520 158 L 620 158"
        stroke="#6ea3ff"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M322 34 L 322 232" stroke="#6ea3ff" strokeWidth="1" strokeDasharray="3 6" />
      <path d="M208 126 L 208 232" stroke="#6ea3ff" strokeWidth="1" strokeDasharray="3 6" />
      <path d="M430 102 L 430 158 L 560 158" stroke="#4a86e8" strokeWidth="1.2" />
      <path d="M8 232 L 620 232" stroke="#6ea3ff" strokeWidth="1" strokeDasharray="8 7" />
      <path d="M208 250 L 322 250" stroke="#6ea3ff" strokeWidth="1" />
      <path d="M208 244 L 208 256 M322 244 L 322 256" stroke="#6ea3ff" strokeWidth="1" />
      <text x="240" y="272" fill="#6ea3ff" fontSize="11" style={{ fontFamily: "var(--font-plex-mono), monospace" }}>
        EST. 2017
      </text>
    </svg>
  );
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const setUser = useAuth((s) => s.setUser);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchId, setBranchId] = useState<string>("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchBranches();
        setBranches(list);
        const remembered = typeof window !== "undefined" ? window.localStorage.getItem("whb_last_branch") : null;
        const rememberedId = remembered ? parseInt(remembered, 10) : NaN;
        if (!Number.isNaN(rememberedId) && list.some((b) => b.id === rememberedId)) {
          setBranchId(String(rememberedId));
        } else if (list[0]) {
          setBranchId(String(list[0].id));
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load branches");
      } finally {
        setInitLoading(false);
      }
    })();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await login(email, parseInt(branchId, 10), password);
      if (!res.status || !res.token) {
        setError(res.message || "Login failed");
        return;
      }
      await fetch("/api/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: res.token, branch_uuid: res.branch_uuid }),
      });
      if (typeof window !== "undefined") window.localStorage.setItem("whb_last_branch", branchId);
      if (res.user) setUser(res.user);
      // Land straight on the dashboard, unless the middleware bounced the
      // user here from a deeper page (?redirect=/hr/leave etc.).
      const redirect = searchParams.get("redirect");
      router.push(redirect && redirect.startsWith("/") && redirect !== "/" ? redirect : "/dashboard");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  const labelClass = "block text-[11px] font-medium uppercase tracking-[0.14em] text-[#5b6b83]";
  const fieldClass =
    "h-11 w-full rounded-md border border-[#cdd7e4] bg-white px-3.5 text-sm text-[#001737] outline-none transition-[border-color,box-shadow] placeholder:text-[#9aa8bc] focus:border-[#0168fa] focus:ring-4 focus:ring-[#0168fa]/15 disabled:cursor-not-allowed disabled:opacity-50";

  const newVersionBadge = (
    <span
      style={mono}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#0168fa]/50 bg-[#0168fa]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#7ab0ff]"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[#37d67a]" />
      New Version
    </span>
  );

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[minmax(0,46fr)_minmax(0,54fr)]">
      {/* ——— Brand panel: the blueprint side ——— */}
      <aside className={`${styles.panel} hidden lg:flex flex-col justify-between p-12 text-white`}>
        <div className={styles.panelGrid} />
        <RooflineSketch />

        <div className={`${styles.fadeUp} relative flex items-center justify-between`}>
          <p style={mono} className="text-[11px] uppercase tracking-[0.28em] text-[#8fb3e8]">
            Wai Hong Brothers · ERP
          </p>
          {newVersionBadge}
        </div>

        <div className="relative">
          <h1 className={`${styles.fadeUp} max-w-[420px] text-[40px] font-semibold leading-[1.15] tracking-[-0.01em]`} style={{ animationDelay: "90ms" }}>
            The new workspace for the day&rsquo;s work.
          </h1>
          <p className={`${styles.fadeUp} mt-4 max-w-[380px] text-[15px] leading-relaxed text-[#a8bdda]`} style={{ animationDelay: "180ms" }}>
            Service, warranty, HR and reporting — rebuilt as one modern system,
            running alongside the classic ERP you know.
          </p>

          <ul className="mt-10 space-y-3.5">
            {[
              ["01", "Service requests, estimates & warranty"],
              ["02", "HR, payroll, claims & leave"],
              ["03", "Live dashboards & login activity"],
            ].map(([num, label], i) => (
              <li
                key={num}
                className={`${styles.fadeUp} flex items-baseline gap-4 text-sm text-[#c6d6ee]`}
                style={{ animationDelay: `${270 + i * 90}ms` }}
              >
                <span style={mono} className="text-[11px] text-[#5f8ed6]">{num}</span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <p className={`${styles.fadeUp} relative text-xs text-[#6d84a8]`} style={{ ...mono, animationDelay: "540ms" }}>
          WAIHONGBROTHERS.COM — SPECIALIST IN WATERPROOFING &amp; ROOFING
        </p>
      </aside>

      {/* ——— Form panel ——— */}
      <main className="flex min-h-screen flex-col items-center justify-center px-6 py-10 lg:min-h-0">
        {/* Compact identity strip on small screens, where the brand panel is hidden. */}
        <div className="mb-8 flex w-full max-w-[400px] items-center justify-between lg:hidden">
          <p style={mono} className="text-[11px] uppercase tracking-[0.24em] text-[#5b6b83]">
            WHB · ERP
          </p>
          <span
            style={mono}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#0168fa]/40 bg-[#0168fa]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#0168fa]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#37d67a]" />
            New Version
          </span>
        </div>

        <div className="w-full max-w-[400px]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo-dark.png" alt="Wai Hong Brothers" className={`${styles.fadeUp} w-[132px]`} />

          <p style={mono} className={`${styles.fadeUp} mt-8 text-[11px] font-medium uppercase tracking-[0.24em] text-[#0168fa]`}>
            Sign in
          </p>
          <h2 className={`${styles.fadeUp} mt-2 text-[26px] font-semibold tracking-[-0.01em] text-[#001737]`} style={{ animationDelay: "60ms" }}>
            Welcome to the new ERP
          </h2>
          <p className={`${styles.fadeUp} mt-1.5 text-sm text-[#5b6b83]`} style={{ animationDelay: "120ms" }}>
            Use your existing WHB credentials — same account, new workspace.
          </p>

          <form onSubmit={handleSubmit} data-testid="login-form" className={`${styles.fadeUp} mt-8 space-y-5`} style={{ animationDelay: "180ms" }}>
            <div className="space-y-1.5">
              <label htmlFor="branch" className={labelClass} style={mono}>Branch</label>
              <select
                id="branch"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                disabled={initLoading || loading}
                required
                className={`${fieldClass} appearance-none pr-9 bg-no-repeat`}
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%230168fa' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "0.75rem 0.5rem",
                }}
              >
                <option value="" disabled>
                  {initLoading ? "Loading…" : "Select branch"}
                </option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.branch_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className={labelClass} style={mono}>Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@waihongbrothers.com"
                autoComplete="email"
                required
                className={fieldClass}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className={labelClass} style={mono}>Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                required
                className={fieldClass}
              />
            </div>

            {error && (
              <div role="alert" className="rounded-md border-l-2 border-red-500 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || initLoading || !branchId}
              className="group flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#0168fa] text-sm font-semibold text-white shadow-lg shadow-[#0168fa]/25 transition-all hover:bg-[#0154cd] hover:shadow-xl hover:shadow-[#0168fa]/30 active:translate-y-px disabled:pointer-events-none disabled:opacity-60"
            >
              {loading ? "Signing In…" : "Sign In"}
              {!loading && (
                <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">→</span>
              )}
            </button>
          </form>

          <div className={`${styles.fadeUp} mt-8 border-t border-[#e3e9f1] pt-5`} style={{ animationDelay: "300ms" }}>
            <p className="text-sm text-[#5b6b83]">
              Looking for the old system?{" "}
              <a
                href="https://erp.waihongbrothers.com"
                className="font-medium text-[#0168fa] underline-offset-4 hover:underline"
              >
                Switch to the classic version
              </a>
            </p>
            <p style={mono} className="mt-5 text-[10px] uppercase tracking-[0.08em] text-[#9aa8bc]">
              © 2023–{year} Wai Hong Brothers Sdn Bhd (1247628T/201701033457)
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function LoginPage() {
  // useSearchParams requires a Suspense boundary during prerender.
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
